import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, History, Plane, Ship, MapPin, Info } from "lucide-react";
import { curSym as sym } from "@/lib/currency";

const TIER_TYPES = ["kg_tiers", "cbm_tiers"];
const RATE_TYPES = ["per_kg", "per_cbm"];
const PRICE_TYPES = ["per_carton", "per_drum", "per_suitcase", "per_palette", "per_unit", "fixed"];

const EMPTY = { hub: "uk", dest_country: "*", dest_city: "", mode: "both", target_type: "all", target_id: "",
  label: "", pricing_type: "per_kg", params: {}, min_charge: "", active: true };
const COUNTRY_HUB = { "Royaume-Uni": "uk", France: "fr" };
// Ville principale par destination (libellés du filtre Destination)
const CITY_OF = { "Côte d'Ivoire": "Abidjan", "Guinée": "Conakry", "Sierra Leone": "Freetown" };

const paramsSummary = (r) => {
  const p = r.params || {};
  const s = sym(r.currency);
  if (RATE_TYPES.includes(r.pricing_type)) return `${p.rate ?? "?"} ${s}/${r.pricing_type === "per_kg" ? "kg" : "CBM"}`;
  if (TIER_TYPES.includes(r.pricing_type)) return (p.tiers || []).map((t) => `${t.min}-${t.max || "∞"}: ${t.price}${s}`).join(" · ");
  if (r.pricing_type === "tv_size") return (p.sizes || []).map((t) => `≤${t.max_size}": ${t.price}${s}`).join(" · ");
  if (r.pricing_type === "quote") return "Sur devis";
  return `${p.price ?? "?"} ${s}`;
};

// Éditeur de paliers (min / max / prix)
const TiersEditor = ({ rows, onChange, unit, cur }) => (
  <div className="space-y-1.5" data-testid="tiers-editor">
    {(rows || []).map((t, i) => (
      <div key={i} className="flex items-center gap-1.5">
        <Input type="number" className="rounded-sm h-8 w-20" placeholder="Min" value={t.min}
          onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, min: Number(e.target.value) } : x)))} />
        <Input type="number" className="rounded-sm h-8 w-20" placeholder="Max (0=∞)" value={t.max}
          onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, max: Number(e.target.value) } : x)))} />
        <Input type="number" className="rounded-sm h-8 w-24" placeholder={`Prix/${unit}`} value={t.price}
          onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, price: Number(e.target.value) } : x)))} />
        <span className="text-xs text-muted-foreground">{cur}/{unit}</span>
        <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-600" onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ))}
    <Button size="sm" variant="outline" data-testid="add-tier" className="rounded-sm h-7 text-xs"
      onClick={() => onChange([...(rows || []), { min: 0, max: 0, price: 0 }])}><Plus className="h-3 w-3 mr-1" /> Palier</Button>
  </div>
);

// Sélecteur recherchable branché sur le Catalogue existant (IDs du Catalogue uniquement).
const CatalogPicker = ({ kind, items, categories = [], value, onChange, onCreated }) => {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState("");
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const list = items
    .filter((x) => x.active !== false)
    .filter((x) => !q || norm(x.name_fr).includes(norm(q)) || norm(x.name_en).includes(norm(q)))
    .sort((a, b) => sort === "az" ? a.name_fr.localeCompare(b.name_fr, "fr") : b.name_fr.localeCompare(a.name_fr, "fr"));
  const selected = items.find((x) => x.id === value);
  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      let created;
      if (kind === "article") {
        if (!newCat) { toast.error("Choisissez la catégorie de l'article"); return; }
        ({ data: created } = await api.post("/admin/catalog/articles", { category_id: newCat, name_fr: name }));
      } else {
        ({ data: created } = await api.post("/admin/catalog/categories", { name_fr: name }));
      }
      toast.success(`${kind === "article" ? "Article ajouté au" : "Catégorie ajoutée au"} Catalogue`);
      setAdding(false); setNewName(""); setQ("");
      onChange(created.id);           // sélection automatique du nouvel élément
      onCreated && onCreated();       // recharge le Catalogue partagé
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div data-testid={`picker-${kind}`}>
      <div className="flex gap-1.5">
        <Input data-testid={`picker-${kind}-search`} className="rounded-sm h-9"
          placeholder={kind === "article" ? "Rechercher un article…" : "Rechercher une catégorie…"}
          value={q} onChange={(e) => setQ(e.target.value)} />
        <Button type="button" size="sm" variant="outline" data-testid={`picker-${kind}-sort`} className="rounded-sm h-9 px-2 text-xs"
          onClick={() => setSort(sort === "az" ? "za" : "az")}>{sort === "az" ? "A→Z" : "Z→A"}</Button>
      </div>
      {selected && <div className="mt-1 text-xs" data-testid={`picker-${kind}-selected`}>Sélection : <b>{selected.name_fr}</b></div>}
      <div className="mt-1.5 max-h-44 overflow-y-auto border border-border bg-card" data-testid={`picker-${kind}-list`}>
        {list.map((x) => (
          <button key={x.id} type="button" data-testid={`picker-${kind}-opt-${x.id}`}
            className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-secondary ${x.id === value ? "bg-secondary font-semibold" : ""}`}
            onClick={() => onChange(x.id)}>{x.name_fr}</button>
        ))}
        {list.length === 0 && !adding && (
          <div className="p-3 text-sm" data-testid={`picker-${kind}-notfound`}>
            <p className="text-muted-foreground">{kind === "article" ? "Article introuvable dans le Catalogue" : "Catégorie introuvable dans le Catalogue"}</p>
            <Button type="button" size="sm" variant="outline" data-testid={`picker-${kind}-add`} className="mt-2 rounded-sm h-8 gap-1"
              onClick={() => { setAdding(true); setNewName(q); }}>
              <Plus className="h-3.5 w-3.5" /> {kind === "article" ? "Ajouter au Catalogue" : "Ajouter une catégorie"}
            </Button>
          </div>
        )}
        {adding && (
          <div className="p-3 space-y-1.5 border-t border-border">
            <Input data-testid={`picker-${kind}-new-name`} className="rounded-sm h-8" placeholder="Nom (FR)"
              value={newName} onChange={(e) => setNewName(e.target.value)} />
            {kind === "article" && (
              <select data-testid="picker-article-new-cat" className="w-full h-8 border border-border bg-card px-2 text-sm"
                value={newCat} onChange={(e) => setNewCat(e.target.value)}>
                <option value="">— catégorie de l'article —</option>
                {categories.filter((c) => c.active !== false).map((c) => <option key={c.id} value={c.id}>{c.name_fr}</option>)}
              </select>
            )}
            <div className="flex gap-1.5">
              <Button type="button" size="sm" data-testid={`picker-${kind}-create`} className="rounded-sm h-8 bg-accent text-white" onClick={create}>Créer</Button>
              <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => setAdding(false)}>Annuler</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Bloc de tarification réutilisable : type + prix + minimums.
// Utilisé une fois (mode Aérien OU Maritime) ou en DEUX blocs séparés (mode « Les deux »).
const PriceBlock = ({ types, cur, value: f, onChange, tid }) => {
  const set = (patch) => onChange({ ...f, ...patch });
  const unit = f.pricing_type.includes("cbm") ? "CBM" : "kg";
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Label className="text-xs">Type de tarification</Label>
        <select data-testid={`${tid}-type`} className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.pricing_type}
          onChange={(e) => set({ pricing_type: e.target.value, params: {} })}>
          {Object.entries(types).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></div>
      {RATE_TYPES.includes(f.pricing_type) && (
        <>
          <div><Label className="text-xs">Prix client ({cur}/{unit})</Label>
            <Input data-testid={`${tid}-rate`} type="number" className="rounded-sm" value={f.params.rate ?? ""} onChange={(e) => set({ params: { ...f.params, rate: Number(e.target.value) } })} /></div>
          <div><Label className="text-xs">Minimum facturable ({unit})</Label>
            <Input type="number" className="rounded-sm" value={f.params[unit === "kg" ? "min_billable" : "min_cbm"] ?? ""}
              onChange={(e) => set({ params: { ...f.params, [unit === "kg" ? "min_billable" : "min_cbm"]: Number(e.target.value) } })} /></div>
        </>
      )}
      {TIER_TYPES.includes(f.pricing_type) && (
        <div className="col-span-2"><Label className="text-xs">Paliers ({unit})</Label>
          <TiersEditor rows={f.params.tiers || []} unit={unit} cur={cur} onChange={(tiers) => set({ params: { ...f.params, tiers } })} /></div>
      )}
      {f.pricing_type === "tv_size" && (
        <div className="col-span-2"><Label className="text-xs">Tailles TV (pouces max → prix)</Label>
          <div className="space-y-1.5">
            {(f.params.sizes || []).map((t, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input type="number" className="rounded-sm h-8 w-24" placeholder="Pouces max" value={t.max_size}
                  onChange={(e) => set({ params: { ...f.params, sizes: f.params.sizes.map((x, j) => (j === i ? { ...x, max_size: Number(e.target.value) } : x)) } })} />
                <Input type="number" className="rounded-sm h-8 w-24" placeholder="Prix" value={t.price}
                  onChange={(e) => set({ params: { ...f.params, sizes: f.params.sizes.map((x, j) => (j === i ? { ...x, price: Number(e.target.value) } : x)) } })} />
                <span className="text-xs text-muted-foreground">{cur}</span>
                <Button size="sm" variant="ghost" className="h-7 px-1.5 text-red-600" onClick={() => set({ params: { ...f.params, sizes: f.params.sizes.filter((_, j) => j !== i) } })}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid={`${tid}-add-tv-size`} className="rounded-sm h-7 text-xs"
              onClick={() => set({ params: { ...f.params, sizes: [...(f.params.sizes || []), { max_size: 0, price: 0 }] } })}><Plus className="h-3 w-3 mr-1" /> Taille</Button>
          </div></div>
      )}
      {PRICE_TYPES.includes(f.pricing_type) && (
        <div><Label className="text-xs">Prix ({cur})</Label>
          <Input data-testid={`${tid}-price`} type="number" className="rounded-sm" value={f.params.price ?? ""} onChange={(e) => set({ params: { ...f.params, price: Number(e.target.value) } })} /></div>
      )}
      {f.pricing_type !== "quote" && (
        <>
          <div><Label className="text-xs">Montant minimum ({cur})</Label>
            <Input data-testid={`${tid}-min-charge`} type="number" className="rounded-sm" value={f.min_charge} onChange={(e) => set({ min_charge: e.target.value })} /></div>
          <div><Label className="text-xs">Coût Colis2Go / unité ({cur}) — interne, jamais visible client</Label>
            <Input data-testid={`${tid}-cost`} type="number" className="rounded-sm" value={f.params.cost ?? ""}
              onChange={(e) => set({ params: { ...f.params, cost: Number(e.target.value) || undefined } })} /></div>
        </>
      )}
    </div>
  );
};

export const AdminTariffs = () => {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState({ categories: [], articles: [] });
  const [cxPol, setCxPol] = useState(null);
  const [fHub, setFHub] = useState("all");
  const [fMode, setFMode] = useState("all");
  const [fDest, setFDest] = useState("all");
  const [genRate, setGenRate] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const PB_EMPTY = { pricing_type: "per_kg", params: {}, min_charge: "" };
  // Mode « Les deux » à la création : deux tarifs SÉPARÉS (aérien / maritime)
  const [fAir, setFAir] = useState(PB_EMPTY);
  const [fSea, setFSea] = useState(PB_EMPTY);
  const [hist, setHist] = useState(null);
  // SOURCES UNIQUES (Phase 5A-1A) : Hubs existants + Pays & villes (Actif + Destination = Oui)
  const [hubs, setHubs] = useState([]);
  const [destCountries, setDestCountries] = useState([]);
  const [destCities, setDestCities] = useState([]);

  const load = () => api.get(`/admin/tariffs?hub=${fHub}&mode=${fMode}&dest=${encodeURIComponent(fDest)}`).then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [fHub, fMode, fDest]); // eslint-disable-line
  useEffect(() => {
    api.get("/admin/catalog").then(({ data }) => setCat(data)).catch(() => {});
    api.get("/admin/cancellation-policy").then(({ data }) => setCxPol(data)).catch(() => {});
    api.get("/admin/hubs").then(({ data }) => setHubs(data)).catch(() => {});
    api.get("/countries?kind=destination").then(({ data }) => setDestCountries(data)).catch(() => {});
  }, []);
  // Villes de la destination choisie — uniquement depuis Pays & villes
  useEffect(() => {
    const c = destCountries.find((x) => x.name === f.dest_country);
    if (c) api.get(`/cities?country_code=${c.code}`).then(({ data }) => setDestCities(data)).catch(() => setDestCities([]));
    else setDestCities([]);
  }, [f.dest_country, destCountries]);
  // Options Hub dérivées des Hubs existants (Admin > Hubs), regroupées par pays du moteur (uk/fr)
  const hubOpts = Object.entries(COUNTRY_HUB).map(([cname, code]) => {
    const names = hubs.filter((h) => h.country === cname && h.active).map((h) => h.name);
    return names.length ? { code, label: `${cname === "Royaume-Uni" ? "HUB UK — £" : "HUB FRANCE — €"} · ${names.join(", ")}` } : null;
  }).filter(Boolean);
  if (!data) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  const types = data.types || {};
  const cur = sym(f.hub === "uk" ? "GBP" : "EUR");
  const unit = f.pricing_type.includes("cbm") ? "CBM" : "kg";

  const save = async () => {
    try {
      if (!editId && f.mode === "both") {
        // « Les deux » : enregistrer DEUX règles distinctes dans le moteur existant
        const base = { hub: f.hub, dest_country: f.dest_country, dest_city: f.dest_city,
          target_type: f.target_type, target_id: f.target_type === "all" ? null : f.target_id,
          label: f.label, active: f.active };
        await api.post("/admin/tariffs", { ...base, mode: "air", pricing_type: fAir.pricing_type, params: fAir.params, min_charge: Number(fAir.min_charge) || 0 });
        await api.post("/admin/tariffs", { ...base, mode: "sea", pricing_type: fSea.pricing_type, params: fSea.params, min_charge: Number(fSea.min_charge) || 0 });
        toast.success("Deux règles créées : Aérien / Express + Maritime");
      } else {
        const body = { ...f, min_charge: Number(f.min_charge) || 0, target_id: f.target_type === "all" ? null : f.target_id };
        if (editId) await api.put(`/admin/tariffs/${editId}`, body);
        else await api.post("/admin/tariffs", body);
        toast.success(editId ? "Règle modifiée (version précédente archivée)" : "Règle créée");
      }
      setOpen(false); setEditId(null); setF(EMPTY); setFAir(PB_EMPTY); setFSea(PB_EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (r) => {
    try { await api.delete(`/admin/tariffs/${r.id}`); toast.success("Règle supprimée (archivée)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const toggle = async (r) => {
    try { await api.put(`/admin/tariffs/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openHistory = async (r) => {
    const { data } = await api.get(`/admin/tariffs/${r.id}/history`);
    setHist({ rule: r, entries: data });
  };
  // Exception par destination : copie d'une règle commune (*) pré-remplie — l'exception est prioritaire
  const openException = (r) => {
    const preset = fDest !== "all" ? fDest : (destCountries[0]?.name || "");
    setF({ ...EMPTY, ...r, id: undefined, dest_country: preset, dest_city: "",
      label: r.label || "", min_charge: r.min_charge || "" });
    setEditId(null); setOpen(true);
  };

  return (
    <div data-testid="tariffs-panel">
      {/* FRAIS D'ANNULATION — configurables par Hub (montant, message, règle) */}
      {cxPol && (
        <div className="mb-5">
          <h3 className="font-heading font-bold">Frais d'annulation (traitement engagé)</h3>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            {["uk", "fr"].map((h) => {
              const p = cxPol[h];
              const s = h === "uk" ? "£" : "€";
              return (
                <div key={h} className="border border-border bg-card p-3" data-testid={`cxpol-${h}`}>
                  <div className="flex items-center justify-between">
                    <Badge className={`rounded-sm ${h === "uk" ? "bg-primary text-white" : "bg-blue-100 text-blue-700"}`}>{h === "uk" ? `HUB UK — ${s}` : `HUB FRANCE — ${s}`}</Badge>
                    <Button size="sm" data-testid={`cxpol-save-${h}`} className="rounded-sm h-7 bg-accent text-white"
                      onClick={async () => {
                        try {
                          await api.put(`/admin/cancellation-policy/${h}`, p);
                          toast.success("Politique d'annulation enregistrée");
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}>Enregistrer</Button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Montant ({s})</Label>
                      <Input data-testid={`cxpol-fee-${h}`} type="number" className="rounded-sm h-8" value={p.fee_amount}
                        onChange={(e) => setCxPol({ ...cxPol, [h]: { ...p, fee_amount: e.target.value } })} /></div>
                    <div><Label className="text-xs">Règle d'application</Label>
                      <select data-testid={`cxpol-rule-${h}`} className="w-full h-8 border border-border bg-card px-2 text-sm" value={p.apply_rule}
                        onChange={(e) => setCxPol({ ...cxPol, [h]: { ...p, apply_rule: e.target.value } })}>
                        <option value="after_processing">Après traitement engagé</option>
                        <option value="always">Toujours</option>
                        <option value="never">Jamais (gratuit)</option>
                      </select></div>
                    <div className="col-span-2"><Label className="text-xs">Message affiché au client</Label>
                      <Input data-testid={`cxpol-msg-${h}`} className="rounded-sm h-8" value={p.message}
                        onChange={(e) => setCxPol({ ...cxPol, [h]: { ...p, message: e.target.value } })} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <select data-testid="trf-filter-hub" className="h-9 border border-border bg-card px-2 text-sm" value={fHub} onChange={(e) => setFHub(e.target.value)}>
            <option value="all">Tous les Hubs</option><option value="uk">HUB UK (£)</option><option value="fr">HUB FRANCE (€)</option>
          </select>
          <select data-testid="trf-filter-mode" className="h-9 border border-border bg-card px-2 text-sm" value={fMode} onChange={(e) => setFMode(e.target.value)}>
            <option value="all">Tous modes</option><option value="air">Aérien / Express</option><option value="sea">Maritime</option><option value="both">Les deux</option>
          </select>
          <div className="flex items-center gap-1.5">
            <Label className="text-xs font-semibold">Destination</Label>
            <select data-testid="trf-filter-dest" className="h-9 border border-border bg-card px-2 text-sm" value={fDest} onChange={(e) => setFDest(e.target.value)}>
              <option value="all">Toutes les destinations</option>
              {destCountries.map((c) => (
                <option key={c.id} value={c.name}>{CITY_OF[c.name] ? `${CITY_OF[c.name]} — ${c.name}` : c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <Button data-testid="trf-add-btn" className="rounded-sm bg-accent text-white gap-1" onClick={() => { setF(EMPTY); setFAir(PB_EMPTY); setFSea(PB_EMPTY); setEditId(null); setOpen(true); }}>
          <Plus className="h-4 w-4" /> Nouvelle règle
        </Button>
      </div>

      {/* Raccourcis destination (image de référence) */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {destCountries.map((c) => CITY_OF[c.name] && (
          <button key={c.id} data-testid={`trf-dest-pill-${CITY_OF[c.name]}`} onClick={() => { setFDest(fDest === c.name ? "all" : c.name); setGenRate(null); }}
            className={`h-8 px-4 text-sm font-semibold border transition-colors ${fDest === c.name ? "bg-accent text-white border-accent" : "bg-card border-border hover:bg-secondary"}`}>
            {CITY_OF[c.name]}
          </button>
        ))}
        {fDest === "all" && <span className="text-xs text-muted-foreground flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Sélectionnez une destination pour afficher et modifier ses tarifs.</span>}
      </div>

      {/* Tarification par destination : tarif général (CBM ou kg) + tarif de secours */}
      {fDest !== "all" && fHub !== "all" && (fMode === "sea" || fMode === "air") && (() => {
        const isGen = (r) => r.hub === fHub && r.mode === fMode && r.target_type === "all" && r.active
          && (fMode === "sea" ? r.pricing_type === "per_cbm" : r.pricing_type === "per_kg");
        const gen = (data.rules || []).find((r) => isGen(r) && r.dest_country === fDest)
          || (data.rules || []).find((r) => isGen(r) && r.dest_country === "*");
        if (!gen) return null;
        const s = sym(gen.currency);
        return (
          <div className="mt-3 border border-border bg-card p-3" data-testid="trf-dest-pricing-card">
            <h4 className="font-heading font-bold text-sm">Tarification {fMode === "sea" ? "maritime" : "aérienne"} par destination — {CITY_OF[fDest] || fDest}</h4>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <div><Label className="text-xs">{fMode === "sea" ? `Tarif CBM (${s}/m³)` : `Tarif au poids (${s}/kg)`}</Label>
                <Input data-testid="trf-gen-rate" type="number" className="rounded-sm h-9 w-36" value={genRate === null ? (gen.params?.rate ?? "") : genRate} onChange={(e) => setGenRate(e.target.value)} /></div>
              <div><Label className="text-xs">Tarif de secours</Label>
                <Input data-testid="trf-gen-fallback" disabled className="rounded-sm h-9 w-36 bg-secondary font-medium" value="Devis requis" /></div>
              <Button data-testid="trf-gen-save" className="rounded-sm bg-accent text-white h-9" onClick={async () => {
                try {
                  await api.put(`/admin/tariffs/${gen.id}`, { params: { ...gen.params, rate: Number(genRate ?? gen.params?.rate) } });
                  toast.success("Tarif général enregistré"); setGenRate(null); load();
                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
              }}>Enregistrer</Button>
              {gen.dest_country === "*" && <span className="text-xs text-muted-foreground">Règle commune aux 3 destinations — utilisez l'épingle <MapPin className="inline h-3 w-3" /> pour créer une exception propre à {CITY_OF[fDest] || fDest}.</span>}
            </div>
          </div>
        );
      })()}

      <div className="mt-3 border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Hub</TableHead><TableHead>Destination</TableHead><TableHead>Mode</TableHead>
            <TableHead>Cible</TableHead><TableHead>Type</TableHead><TableHead>Tarif</TableHead>
            <TableHead>Min</TableHead><TableHead>Actif</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data.rules || []).map((r) => (
              <TableRow key={r.id} data-testid={`trf-row-${r.id}`}>
                <TableCell><Badge className={`rounded-sm ${r.hub === "uk" ? "bg-primary text-white" : "bg-blue-100 text-blue-700"}`}>{r.hub === "uk" ? "UK £" : "FR €"}</Badge></TableCell>
                <TableCell className="font-semibold">{r.dest_country === "*" ? <span>Commune<span className="block text-xs font-normal text-muted-foreground">toutes destinations</span></span> : (r.dest_city || r.dest_country)}{r.dest_country !== "*" && r.dest_city ? <span className="block text-xs font-normal text-muted-foreground">{r.dest_country}</span> : null}</TableCell>
                <TableCell className="text-xs">{r.mode === "both" ? "Air + Mer" : r.mode === "air" ? "Aérien" : "Maritime"}</TableCell>
                <TableCell className="text-xs">{r.target_name}</TableCell>
                <TableCell className="text-xs font-semibold">{r.type_label}</TableCell>
                <TableCell className="text-xs max-w-[220px] truncate" title={paramsSummary(r)}>{paramsSummary(r)}</TableCell>
                <TableCell className="text-xs">{r.min_charge > 0 ? `${r.min_charge} ${sym(r.currency)}` : "—"}</TableCell>
                <TableCell><Switch data-testid={`trf-active-${r.id}`} checked={!!r.active} onCheckedChange={() => toggle(r)} /></TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  {r.dest_country === "*" && (
                    <Button size="sm" variant="ghost" data-testid={`trf-exception-${r.id}`} className="h-7 px-1.5 text-primary" title="Créer une exception pour une destination" onClick={() => openException(r)}><MapPin className="h-3.5 w-3.5" /></Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`trf-hist-${r.id}`} className="h-7 px-1.5" onClick={() => openHistory(r)}><History className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`trf-edit-${r.id}`} className="h-7 px-1.5" onClick={() => { setF({ ...EMPTY, ...r, min_charge: r.min_charge || "" }); setEditId(r.id); setOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`trf-del-${r.id}`} className="h-7 px-1.5 text-red-600" onClick={() => del(r)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {(data.rules || []).length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Aucune règle tarifaire.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Bandeau priorité des règles (image de référence) */}
      <div className="mt-3 border border-border bg-secondary/50 p-3 text-xs flex items-center gap-2" data-testid="trf-priority-info">
        <Info className="h-4 w-4 shrink-0 text-primary" />
        <span><b>Priorité :</b> prix fixe article → tarif propre à la destination → tarif CBM/kg du trajet → tarif général → devis requis. Un prix fixe n'est <b>jamais additionné</b> au tarif au kg ou au CBM.</span>
      </div>

      {/* Dialog création / modification */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); setEditId(null); } }}>
        <DialogContent className="rounded-sm max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="trf-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifier la règle tarifaire" : "Nouvelle règle tarifaire"}</DialogTitle>
            <DialogDescription>Devise automatique selon le Hub (UK → £, France → €). Toute modification est archivée.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Hub (devise auto)</Label>
              <select data-testid="trf-hub" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.hub} onChange={(e) => setF({ ...f, hub: e.target.value })}>
                {hubOpts.length === 0 && <option value={f.hub}>Chargement des hubs…</option>}
                {hubOpts.map((h) => <option key={h.code} value={h.code}>{h.label}</option>)}
              </select></div>
            <div><Label className="text-xs">Destination (Pays &amp; villes : Actif + Destination)</Label>
              <select data-testid="trf-dest" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.dest_country}
                onChange={(e) => setF({ ...f, dest_country: e.target.value, dest_city: "" })}>
                <option value="*">Toutes les destinations</option>
                {destCountries.map((c) => <option key={c.id} value={c.name}>{c.flag} {c.name}</option>)}
              </select></div>
            {f.dest_country !== "*" && destCities.length > 0 && (
              <div><Label className="text-xs">Ville (optionnel — Pays &amp; villes)</Label>
                <select data-testid="trf-dest-city" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.dest_city || ""}
                  onChange={(e) => setF({ ...f, dest_city: e.target.value })}>
                  <option value="">Toutes les villes</option>
                  {destCities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select></div>
            )}
            <div><Label className="text-xs">Mode</Label>
              <select data-testid="trf-mode" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
                <option value="both">Les deux</option><option value="air">Aérien / Express</option><option value="sea">Maritime</option>
              </select></div>
            <div><Label className="text-xs">Cible</Label>
              <select data-testid="trf-target-type" className="w-full h-9 border border-border bg-card px-2 text-sm" value={f.target_type} onChange={(e) => setF({ ...f, target_type: e.target.value, target_id: "" })}>
                <option value="all">Générale (tout le corridor)</option><option value="category">Catégorie</option><option value="article">Article</option>
              </select></div>
            {f.target_type === "category" && (
              <div className="col-span-2"><Label className="text-xs">Catégorie (Catalogue)</Label>
                <CatalogPicker kind="category" items={cat.categories} value={f.target_id}
                  onChange={(id) => setF({ ...f, target_id: id })}
                  onCreated={() => api.get("/admin/catalog").then(({ data }) => setCat(data))} /></div>
            )}
            {f.target_type === "article" && (
              <div className="col-span-2"><Label className="text-xs">Article (Catalogue)</Label>
                <CatalogPicker kind="article" items={cat.articles.filter((a) => a.scope === "global")}
                  categories={cat.categories} value={f.target_id}
                  onChange={(id) => setF({ ...f, target_id: id })}
                  onCreated={() => api.get("/admin/catalog").then(({ data }) => setCat(data))} /></div>
            )}
            {(editId || f.mode !== "both") ? (
              <div className="col-span-2">
                <PriceBlock types={types} cur={cur} tid="trf"
                  value={{ pricing_type: f.pricing_type, params: f.params, min_charge: f.min_charge }}
                  onChange={(v) => setF({ ...f, ...v })} />
              </div>
            ) : (
              <>
                <div className="col-span-2 border border-border p-3" data-testid="trf-block-air">
                  <div className="flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider mb-2"><Plane className="h-3.5 w-3.5" /> Tarif Aérien / Express</div>
                  <PriceBlock types={types} cur={cur} tid="trf-air" value={fAir} onChange={setFAir} />
                </div>
                <div className="col-span-2 border border-border p-3" data-testid="trf-block-sea">
                  <div className="flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider mb-2"><Ship className="h-3.5 w-3.5" /> Tarif Maritime</div>
                  <PriceBlock types={types} cur={cur} tid="trf-sea" value={fSea} onChange={setFSea} />
                </div>
              </>
            )}
            <div className="col-span-2"><Label className="text-xs">Libellé (optionnel)</Label>
              <Input data-testid="trf-label" className="rounded-sm" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setOpen(false); setEditId(null); }}>Annuler</Button>
            <Button data-testid="trf-save" className="rounded-sm bg-accent text-white" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historique d'une règle */}
      <Dialog open={!!hist} onOpenChange={(v) => !v && setHist(null)}>
        <DialogContent className="rounded-sm max-h-[80vh] overflow-y-auto" data-testid="trf-history-dialog">
          <DialogHeader><DialogTitle>Historique — {hist?.rule?.label || hist?.rule?.type_label}</DialogTitle>
            <DialogDescription>Versions précédentes archivées de cette règle tarifaire.</DialogDescription>
          </DialogHeader>
          {(hist?.entries || []).length === 0 && <p className="text-sm text-muted-foreground">Aucune modification enregistrée.</p>}
          <div className="space-y-2">
            {(hist?.entries || []).map((h) => (
              <div key={h.id} className="border border-border p-2.5 text-xs">
                <div className="flex justify-between">
                  <Badge className={`rounded-sm ${h.action === "delete" ? "bg-red-100 text-red-700" : "bg-secondary text-primary"}`}>{h.action === "delete" ? "Suppression" : "Modification"}</Badge>
                  <span className="text-muted-foreground">{(h.changed_at || "").slice(0, 16).replace("T", " ")} · {h.changed_by}</span>
                </div>
                <div className="mt-1.5 text-muted-foreground">Version précédente : <b>{paramsSummary(h.snapshot)}</b>
                  {h.snapshot.min_charge > 0 && <> · min {h.snapshot.min_charge} {sym(h.snapshot.currency)}</>} · {h.snapshot.active ? "active" : "inactive"}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
